import { useEffect, useMemo, useState} from "react";

const ANIMATION_TARGET = 30; // Target percentage for animation
const ANIMATION_DURATION = 300000; // 5 minutes

const calculateAnimationProgress = (elapsed: number): number =>
    Math.min((elapsed / ANIMATION_DURATION) * ANIMATION_TARGET, ANIMATION_TARGET);

export const useAnimatedProgress = (createdTs: string, currentProgress: number, isActive: boolean) => {
  const [animationProgress, setAnimationProgress] = useState<number>(0);

  const calculateProgress = (elapsed: number) => {
    // Protect against NaN
    if (isNaN(elapsed) || elapsed < 0) {
      return ANIMATION_TARGET; // Return 30% when date is invalid
    }
    const progress = calculateAnimationProgress(elapsed);

    if (elapsed >= ANIMATION_DURATION && currentProgress < ANIMATION_TARGET) {
      return ANIMATION_TARGET;
    } else {
      return progress;
    }
  };

  useEffect(() => {
    if (!isActive) {
      setAnimationProgress(0);
      return;
    }
    
    // If createdTs is empty or invalid, show 30% progress
    if (!createdTs || createdTs.trim() === "") {
      setAnimationProgress(ANIMATION_TARGET);
      return;
    }
    
    const createdDate = new Date(createdTs);
    // Check if date is valid
    if (isNaN(createdDate.getTime())) {
      setAnimationProgress(ANIMATION_TARGET);
      return;
    }
    
    const elapsed = Date.now() - createdDate.getTime();
    const progress = calculateProgress(elapsed);
    setAnimationProgress(progress);

    if (elapsed >= ANIMATION_DURATION) {
      return;
    }

    const interval = setInterval(() => {
      const newCreatedDate = new Date(createdTs);
      if (isNaN(newCreatedDate.getTime())) {
        setAnimationProgress(ANIMATION_TARGET);
        clearInterval(interval);
        return;
      }
      
      const newElapsed = Date.now() - newCreatedDate.getTime();
      const newProgress = calculateProgress(newElapsed);
      setAnimationProgress(newProgress);

      if (newElapsed >= ANIMATION_DURATION) {
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isActive, calculateProgress, createdTs]);

    return useMemo(() => {
      const result = Math.max(currentProgress, animationProgress);
      // Protect against NaN in final result - if NaN, show 30% as fallback
      return isNaN(result) ? ANIMATION_TARGET : result;
  }, [currentProgress, animationProgress]);
};
